
import React from "react";
import { Helmet } from "react-helmet-async";

interface StructuredDataProps {
  data: Record<string, any> | Record<string, any>[];
  type?: "application/ld+json" | string;
}

/**
 * Composant pour injecter des données structurées Schema.org dans le head du document
 * Supporte maintenant les tableaux de données structurées multiples
 * @param {Object|Array} data - Les données structurées à injecter (format JSON ou tableau)
 * @param {string} type - Type MIME des données (par défaut application/ld+json)
 */
const StructuredData: React.FC<StructuredDataProps> = ({ 
  data, 
  type = "application/ld+json" 
}) => {
  // Fonction de validation améliorée pour un objet de données
  const validateDataObject = (dataObj: Record<string, any>): boolean => {
    // Vérifier si l'objet est vide
    if (!dataObj || Object.keys(dataObj).length === 0) {
      return false;
    }
    
    // Vérification de la présence du contexte Schema.org
    if (!dataObj["@context"] || !dataObj["@context"].includes("schema.org")) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("StructuredData: Le contexte Schema.org est manquant ou incorrect", dataObj);
      }
      return false;
    }
    
    // Vérification de la présence du type
    if (!dataObj["@type"]) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("StructuredData: Le type Schema.org est manquant", dataObj);
      }
      return false;
    }
    
    return true;
  };
  
  // Validation et filtrage des données
  let validData: Record<string, any> | Record<string, any>[] | null = null;
  
  if (Array.isArray(data)) {
    const validArray = data.filter(validateDataObject);
    if (validArray.length > 0) {
      validData = validArray;
    }
  } else if (validateDataObject(data)) {
    validData = data;
  }
  
  // Ne rien rendre si aucune donnée valide
  if (!validData) {
    if (process.env.NODE_ENV === 'development') {
      console.warn("StructuredData: Aucune donnée valide n'a été trouvée");
    }
    return null;
  }

  return (
    <Helmet>
      <script type={type}>
        {JSON.stringify(validData, null, process.env.NODE_ENV === 'development' ? 2 : 0)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
